import { TileSelectionSection } from '@features/instructors/components/header/actions-tiles/tile-selection-section/TileSelectionSection';
import { InstructorsTile } from '@features/instructors/components/tile/InstructorsTile';
import { useManageInstructorsSelection } from '@features/instructors/contexts/manage-instructors-selection/useManageInstructorsSelection';
import { useInstructorsUnapprovedData } from '@features/instructors/contexts/unapproved/useInstructorsUnapprovedData';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { PanelLoadingState } from '@shared/components/panel-loading-state/PanelLoadingState';
import * as React from 'react';

import type { ManageInstructorsPanelContentProps } from './ManageInstructorsPanelContent.types';

import {
  mapUnapprovedToInstructor,
  SECTION_CONFIGS,
} from '../../InstructorManageInstructorsPanel.utils';
import styles from './ManageInstructorsPanelContent.module.css';

const ManageInstructorsPanelContent = React.memo(
  ({
    canManageInstructors,
  }: Readonly<ManageInstructorsPanelContentProps>): React.ReactElement => {
    const { data, isLoading, isError } = useInstructorsUnapprovedData();
    const { selectedIds, handleSelectionChange, selectAll, deselectAll } =
      useManageInstructorsSelection();

    const hasInstructorsToManage = SECTION_CONFIGS.some(
      section => (data?.[section.key].length ?? 0) > 0
    );

    if (isLoading) return <PanelLoadingState />;
    if (isError) return <InlineFeedback type="data-error" />;
    if (!hasInstructorsToManage) return <InlineFeedback type="so-empty" />;

    return (
      <div
        className={styles.instructorsToManageContainer}
        data-testid="manage-instructors-panel-content"
      >
        {SECTION_CONFIGS.map(section => (
          <TileSelectionSection
            key={section.key}
            title={section.title}
            dataTestId={section.dataTestId}
            selectedCount={selectedIds[section.key].size}
            totalCount={data?.[section.key].length}
            onSelectAll={selectAll[section.key]}
            onDeselectAll={deselectAll[section.key]}
            showSelectAll={canManageInstructors}
          >
            {data?.[section.key].map(unapprovedInstructor =>
              canManageInstructors ? (
                <InstructorsTile
                  selectable
                  clickable
                  selected={selectedIds[section.key].has(
                    unapprovedInstructor.instructorId
                  )}
                  instructor={mapUnapprovedToInstructor(unapprovedInstructor)}
                  data-testid="instructor-tile"
                  key={unapprovedInstructor.instructorId}
                  onSelectionChange={(id, selected) =>
                    handleSelectionChange(section.key, id, selected)
                  }
                />
              ) : (
                <InstructorsTile
                  clickable
                  instructor={mapUnapprovedToInstructor(unapprovedInstructor)}
                  data-testid="instructor-tile"
                  key={unapprovedInstructor.instructorId}
                />
              )
            )}
          </TileSelectionSection>
        ))}
      </div>
    );
  }
);

ManageInstructorsPanelContent.displayName = 'ManageInstructorsPanelContent';
export { ManageInstructorsPanelContent };
